import Button from '@/components/Button'
import IcoMoon from '@/components/IcoMoon'
import { Stack } from '@/components/Layout'
import Text from '@/components/Text'
import HelenInfoBlock from '@/compositions/HelenInfoBlock'
import { coreLayoutContainer, coreLayoutSection, rbp } from '@/constants/measured-scope'
import { useAuth } from '@/context/auth'
import { useFeatureToggle } from '@/context/featureToggles'
import { HeroInfoSection } from '@/shared/api-generated-types'
import { useMediaQuery } from '@react-hookz/web'
import DOMPurify from 'isomorphic-dompurify'
import Link from 'next/link'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as styles from './style'

export interface Props extends Partial<HeroInfoSection> {
  className?: string
}

const HeroInfoBlock = ({ className = '', ...props }: Props): JSX.Element => {
  const showHelenTab = useFeatureToggle('helen_tab')
  const { t } = useTranslation('common')
  const context = useAuth()
  const isSmallDevice = useMediaQuery(`only screen and (max-width : ${rbp.smallTablet})`, {
    initializeWithValue: false,
  })
  const hideInfoForAuthUser = useMemo(() => {
    if (isSmallDevice && context.state.user) {
      return true
    }
    return false
  }, [context, isSmallDevice])

  if (showHelenTab)
    return (
      <HelenInfoBlock
        userStats={props.content?.userStats}
        video={props.content?.video}
        navigationActions={props.content?.navigationActions}
      />
    )
  return (
    <header className={`${coreLayoutSection}`}>
      {/* Rendering "logged-in" header logic off of userStats prop as this prop will only be true if the user is logged in. */}
      {!context.state.user ? (
        <Stack x className={`${coreLayoutContainer} ${styles.root}`} alignItems="center" spaceBetweenX="lg">
          <Stack className={styles.ctaBlock} spaceBetweenY="sm" y>
            {props.content?.title ? (
              <Text
                weight="lg"
                size="xl"
                responsiveSize={{
                  lg: 'xxl',
                  xxl: 'xxxl',
                }}
                className="fc-primary"
              >
                <span dangerouslySetInnerHTML={{ __html: props.content.title }} />
              </Text>
            ) : null}

            {props.content?.description && !hideInfoForAuthUser ? (
              <Text tag="p" className={styles.desc} weight="sm">
                <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(props.content.description) }} />
              </Text>
            ) : null}
            {props.content?.primaryAction ? (
              <div className={`${styles.ctaButtons} mt-xs`}>
                <Button
                  theme="secondary"
                  label={props.content.primaryAction.label}
                  className={styles.ctaButton}
                  href={props.content.primaryAction.url}
                  size="md"
                  component="a"
                />
                {props.content.navigationActions && props.content.navigationActions.length ? (
                  <div>
                    {props.content.navigationActions.map((action, index) => (
                      <Button
                        label={action.label}
                        className={styles.ctaButton}
                        size="md"
                        component="link"
                        theme="clear"
                        href={action.url}
                        key={index}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </Stack>
          {props.content?.showVideo && props.content?.video && !hideInfoForAuthUser ? (
            <div className={styles.videoContainer}>
              <div>
                <video poster={props.content.video.posterUrl} data-testid="video" className={styles.video} controls>
                  <source src={`${props.content.video.srcUrl}#t=0.001`} type="video/mp4" />
                </video>
              </div>
            </div>
          ) : null}
        </Stack>
      ) : (
        <Stack x className={`${coreLayoutContainer} ${styles.header}`} alignItems="start">
          <Stack>
            <div>
              {props.content?.title ? (
                <Text weight="lg" size="lg" responsiveSize={{ sm: 'xl', xl: 'xxl' }} className={`fc-primary`}>
                  {props.content.title}
                </Text>
              ) : null}
            </div>
            <Link href="/notes" className={`${styles.profileLink} fs-xxs fs-xs-gte-lg`} prefetch={false}>
              <IcoMoon icon="user" className="mr-xxxs" />
              {t('cta.viewProfile')}
            </Link>
          </Stack>
        </Stack>
      )}
    </header>
  )
}
export default HeroInfoBlock
